import { Writeable } from 'ia-react-core/~/interfaces/Writeable';
import resolveScope from 'ia-react-core/~/utils/redux/_resolveScope';
import get from 'lodash/get';
import { Dispatch } from 'redux';
import setFormValuesById from 'ia-react-core/~/actions/form-fn/setFormValuesById';
import setControlValueById from 'ia-react-core/~/actions/form-fn/setControlValueById';
import * as clauses from './clauses';

export interface BehaviorResolveOptions<State> {
  state: State;
  scope: string[];
  valuesDst: string;
  formReducerKey: string;
  ancestorFormReducerScope: string[];
  defaultScope: 'form' | 'values' | 'state';
  defaultInput?: string;
}

export class Condition {
  not = false;
  operator: 'and' | 'or' = 'and';
  clauses: (((...args: unknown[]) => boolean | Condition) | ((...args: unknown[]) => (...args: unknown[]) => boolean | Condition))[] = [];
  inputs?: string[];
  defaultClause = clauses.truthy();

  constructor(options: Partial<Condition> = {}) {
    if ('clauses' in options) this.clauses = options?.clauses;
    if ('inputs' in options) this.inputs = options?.inputs;
  }

  #getPath<State>(input: string, { scope, valuesDst, formReducerKey, ancestorFormReducerScope, defaultScope }: Omit<BehaviorResolveOptions<State>, 'state'>) {
    if (input.startsWith('@form/') || (!input.startsWith('@') && defaultScope === 'form')) {
      const path = input.replace(/^@form\//, '');
      return resolveScope(path, [...ancestorFormReducerScope, formReducerKey]);
    }

    if (input.startsWith('@values/') || (!input.startsWith('@') && defaultScope === 'values')) {
      const path = input.replace(/^@values\//, '');
      return resolveScope(path, [...ancestorFormReducerScope, valuesDst]);
    }

    if (input.startsWith('@') && !input.startsWith('@state/')) {
      throw new Error(`Unknown scope on input "${input}"`);
    }

    const path = input.replace(/^@state\//, '');
    return resolveScope(path, scope);
  }

  resolve<State>(inputs: string[], { state, scope, valuesDst, formReducerKey, ancestorFormReducerScope, defaultScope }: BehaviorResolveOptions<State>): boolean {
    const baseInputs = this.inputs || inputs;

    const resolvedInputs = baseInputs.map((input) => {
      const path = this.#getPath(input, { scope, ancestorFormReducerScope, formReducerKey, valuesDst, defaultScope });
      return path === '' ? state : get(state, path);
    });
    const resolveClauses = this.clauses.length ? this.clauses : [this.defaultClause];

    return resolveClauses.every((clause) => {
      if (clause instanceof Condition) {
        return clause.resolve(inputs, { state, scope, valuesDst, formReducerKey, ancestorFormReducerScope, defaultScope });
      }
      return clause(...resolvedInputs);
    });
  }
}

export class Assertion {
  inputs?: string[] = [];
  condition?: Condition = new Condition();
  result?: any = true;
  alternateResult?: any = false;

  constructor(options: Partial<Assertion> = {}) {
    if ('inputs' in options) this.inputs = options?.inputs;
    if ('condition' in options) this.condition = options?.condition;
    if ('result' in options) this.result = options?.result;
    if ('alternateResult' in options) this.alternateResult = options?.alternateResult;
  }
}

export interface BehaviorOptions {
  asserts?: Assertion[];
}

type Clauses = typeof clauses;

type ClauseMethods = Writeable<{
  [clauseName in keyof typeof clauses]: (...args: Parameters<Clauses[clauseName]>) => BehaviorInstance<boolean>;
}>;

export class BaseBehavior<Value> {
  asserts: Assertion[];

  validationMessage: null | string | false = null;

  // This prop is just to ensure validation of "Value"
  value: Value;

  callbacks: ((options: { event: Event; state: object; dispatch: Dispatch; parentFormId: number }) => void)[] = [];

  constructor({ asserts = [] }: BehaviorOptions = {}) {
    this.asserts = asserts;
    if (!this.asserts.length) asserts.push(new Assertion());
  }

  when(...inputs: string[]) {
    const newAssert = new Assertion({ inputs });
    this.asserts.push(newAssert);
    return this;
  }

  then<Result>(result: Result) {
    const currentAssert = this.asserts.at(-1);
    currentAssert.result = result;
    currentAssert.alternateResult = undefined;
    return this as unknown as BehaviorInstance<Result extends number ? number : Result>;
  }

  test(fn: (...inputs: any) => boolean) {
    const currentAssert = this.asserts.at(-1);
    currentAssert.condition.clauses.push(fn);
    return this;
  }

  otherwise<Result>(result: Result) {
    const currentAssert = this.asserts.at(-1);
    currentAssert.alternateResult = result;
    return this as unknown as BehaviorInstance<Result>;
  }

  message(message: string | false) {
    this.validationMessage = message;
    return this;
  }

  unset(...inputs: string[]) {
    this.callbacks.push(
      ({ dispatch, parentFormId }) => {
        if (inputs.length) {
          inputs.forEach((input) => {
            dispatch(setControlValueById(parentFormId, input, undefined, { override: true }));
          });
        } else {
          dispatch(setFormValuesById(parentFormId, undefined, { override: true }));
        }
      },
    );

    return this;
  }

  set(values: any): this;
  set(input: string, value: any): this;
  set(input: string, value?: any) {
    this.callbacks.push(
      ({ dispatch, parentFormId }) => {
        if (typeof value === 'undefined') {
          dispatch(setFormValuesById(parentFormId, input as Object, { override: true }));
        } else {
          dispatch(setControlValueById(parentFormId, input, value, { override: true }));
        }
      },
    );
    return this;
  }

  merge(values: any): this;
  merge(input: string, value: any): this;
  merge(input: string, value?: any) {
    this.callbacks.push(
      ({ dispatch, parentFormId }) => {
        if (typeof value === 'undefined') {
          dispatch(setFormValuesById(parentFormId, input as Object));
        } else {
          dispatch(setControlValueById(parentFormId, input, value));
        }
      },
    );
    return this;
  }

  resolve<State extends object>({ state, scope, valuesDst, formReducerKey, ancestorFormReducerScope, defaultScope, defaultInput }: BehaviorResolveOptions<State>) {
    for (let i = 0, l = this.asserts.length; i < l; i++) {
      const assert = this.asserts[i];
      const defaultedInputs = (assert.inputs.length || !defaultInput) ? assert.inputs : [defaultInput];

      const conditionResult = assert.condition.resolve<State>(defaultedInputs, {
        state, scope, valuesDst, formReducerKey, ancestorFormReducerScope, defaultScope,
      });

      if (conditionResult) {
        return assert.result;
      } if (typeof assert.alternateResult !== 'undefined') {
        return assert.alternateResult;
      }
    }
    return undefined;
  }
}

export type BehaviorInstance<Value> = BaseBehavior<Value> & ClauseMethods;
type Behavior = new <Value>(...args: ConstructorParameters<typeof BaseBehavior<Value>>) => BehaviorInstance<Value>;

const Behavior = BaseBehavior as unknown as Behavior;

const clauseNames = Object.keys(clauses) as (keyof typeof clauses)[];
clauseNames.forEach((clauseName) => {
  const clause = clauses[clauseName] as (...args: unknown[]) => (...args: unknown[]) => boolean | Condition;
  // eslint-disable-next-line func-names
  Behavior.prototype[clauseName] = function (...args: unknown[]) {
    const currentAssert = this.asserts.at(-1);
    const { inputs } = currentAssert;

    const clauseContext = {
      originalInputs: [...inputs],
    };

    const currentClause = clause.call(clauseContext, ...args);
    currentAssert.condition.clauses.push(currentClause);

    return this;
  };
});

export default Behavior;
